import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type UseMetricChartResult, useMetricChart } from '@/components/charts/generic';
import type { RespirationDataPoint } from '@/domain/behavior';
import { parseLocale } from '@/utils/localeFormatters';

import { buildRespirationOverviewSpec } from '../../BehaviorRespiration/respirationChartSpec';

interface UseOverviewRespirationChartDataParams {
    respirationDataPoints: RespirationDataPoint[];
}

interface UseOverviewRespirationChartDataReturn {
    chart: UseMetricChartResult<'line'>;
    showBreathingRate: boolean;
    setShowBreathingRate: (value: boolean) => void;
}

/**
 * The dashboard's respiration chart: the active and passive shares by default, the breathing
 * rate when the switch is on, each with oxygen when there is any.
 */
export function useOverviewRespirationChartData({
    respirationDataPoints,
}: UseOverviewRespirationChartDataParams): UseOverviewRespirationChartDataReturn {
    const { t, i18n } = useTranslation('behaviorModule');
    const [showBreathingRate, setShowBreathingRate] = useState(false);

    const spec = useMemo(
        () =>
            buildRespirationOverviewSpec({
                points: respirationDataPoints,
                t,
                locale: parseLocale(i18n.language),
                showBreathingRate,
            }),
        [respirationDataPoints, t, i18n.language, showBreathingRate]
    );

    return { chart: useMetricChart(spec), showBreathingRate, setShowBreathingRate };
}
